import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

const STORAGE_KEY = "karyna-country";
const SYNC_EVENT = "karyna-country-sync";

const Flag = ({ country }) => {
  if (country === "fr")
    return (
      <svg viewBox="0 0 12 8" className="w-4 h-3 shrink-0 rounded-[1px] shadow-sm" aria-hidden="true">
        <rect width="4" height="8" x="0" fill="#0055A4" />
        <rect width="4" height="8" x="4" fill="#FFFFFF" />
        <rect width="4" height="8" x="8" fill="#EF4135" />
      </svg>
    );
  if (country === "be")
    return (
      <svg viewBox="0 0 12 8" className="w-4 h-3 shrink-0 rounded-[1px] shadow-sm" aria-hidden="true">
        <rect width="4" height="8" x="0" fill="#1B1714" />
        <rect width="4" height="8" x="4" fill="#FDDA24" />
        <rect width="4" height="8" x="8" fill="#EF3340" />
      </svg>
    );
  if (country === "es")
    return (
      <svg viewBox="0 0 12 8" className="w-4 h-3 shrink-0 rounded-[1px] shadow-sm" aria-hidden="true">
        <rect width="12" height="2" y="0" fill="#AA151B" />
        <rect width="12" height="4" y="2" fill="#F1BF00" />
        <rect width="12" height="2" y="6" fill="#AA151B" />
      </svg>
    );
  return null;
};

const COUNTRIES = [
  { value: "fr", label: "France | EUR €" },
  { value: "be", label: "Belgique | EUR €" },
  { value: "es", label: "Espagne | EUR €" },
];

export default function CountrySelector({ variant = "footer" }) {
  const [country, setCountry] = useState(() => localStorage.getItem(STORAGE_KEY) || "fr");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onSync = (e) => setCountry(e.detail);
    window.addEventListener(SYNC_EVENT, onSync);
    return () => window.removeEventListener(SYNC_EVENT, onSync);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const choose = (value) => {
    setCountry(value);
    localStorage.setItem(STORAGE_KEY, value);
    window.dispatchEvent(new CustomEvent(SYNC_EVENT, { detail: value }));
    setOpen(false);
  };

  const dark = variant === "footer";
  const current = COUNTRIES.find((c) => c.value === country) || COUNTRIES[0];

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        data-testid={dark ? "country-selector" : "country-selector-header"}
        onClick={() => setOpen(!open)}
        aria-label="Pays ou région de livraison"
        aria-expanded={open}
        className={`flex items-center gap-2 bg-transparent border hover:border-gold transition-colors duration-300 px-2.5 text-[11px] tracking-normal font-light cursor-pointer outline-none w-[150px] ${
          dark ? "border-ivory/30 text-ivory py-2.5" : "border-choco/30 text-choco py-2"
        }`}
      >
        <Flag country={current.value} />
        <span className="flex-1 text-left truncate">{current.label}</span>
        <ChevronDown
          size={14}
          className={`opacity-60 transition-transform duration-300 ${open ? "rotate-180" : ""} ${dark ? "" : "text-choco"}`}
        />
      </button>
      {open && (
        <div
          data-testid={dark ? "country-menu" : "country-menu-header"}
          className={`absolute left-0 w-full bg-ivory border border-choco/15 shadow-lg shadow-choco/10 z-50 ${
            dark ? "bottom-full mb-2" : "top-full mt-2"
          }`}
        >
          {COUNTRIES.map((c) => (
            <button
              key={c.value}
              type="button"
              data-testid={`country-option-${c.value}`}
              onClick={() => choose(c.value)}
              className={`w-full flex items-center gap-2 px-2.5 py-2.5 text-[11px] font-light transition-colors duration-200 hover:bg-gold/10 ${
                c.value === country ? "text-gold" : "text-choco"
              }`}
            >
              <Flag country={c.value} />
              <span>{c.label}</span>
            </button>
          ))}
          <div
            data-testid="country-option-eu"
            className="px-2.5 py-2.5 text-[11px] font-light text-choco/45 border-t border-choco/10 cursor-not-allowed"
          >
            Europe — bientôt disponible
          </div>
        </div>
      )}
    </div>
  );
}
